"use client";

import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

export interface SessionHistoryContextValue {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
}

const SessionHistoryContext = createContext<SessionHistoryContextValue | null>(null);

/**
 * Shared open/close state for the Session History (left) panel so the toggle can
 * live in the work-product nav while the drawer itself renders inside Chat.
 * Mirrors the ToolsDrawer (Favorites) provider pattern.
 */
export function SessionHistoryProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

  const value = useMemo<SessionHistoryContextValue>(
    () => ({ isOpen, open, close, toggle }),
    [isOpen, open, close, toggle],
  );

  return (
    <SessionHistoryContext.Provider value={value}>
      {children}
    </SessionHistoryContext.Provider>
  );
}

export function useSessionHistory(): SessionHistoryContextValue | null {
  return useContext(SessionHistoryContext);
}
